import React, { useEffect, useRef, useState } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import { useServerList, type ServerListQuery, type ServerPage } from '../../hooks/useServerList';

/**
 * Pick one record from a list that lives on the server: users, patients,
 * catalogs, anything that can outgrow one page.
 *
 * Use this instead of a <select> filled by one fetch. That pattern silently
 * drops every record past the endpoint's page cap (patients past the newest 50,
 * AMs past 500, catalogs past 10), and a search box over it only searches what
 * was loaded. Here every keystroke searches the server, and when more matches
 * exist than are shown the dropdown says so. See docs/frontend-lists-and-search.md.
 */
export interface AsyncSearchSelectProps<T, F = Record<string, never>> {
  /** Fetch one page of matches for the search text (server-side search). */
  fetchPage: (query: ServerListQuery<F>) => Promise<ServerPage<T>>;
  filters?: F;
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  /** Optional second line under the label. */
  getDescription?: (item: T) => string | undefined;
  value: T | null;
  onChange: (item: T | null) => void;
  placeholder?: string;
  /** What the records are called, for "Showing 25 of 340 clinics". */
  noun?: string;
  disabled?: boolean;
  pageSize?: number;
  'aria-label'?: string;
}

export function AsyncSearchSelect<T, F = Record<string, never>>({
  fetchPage, filters, getKey, getLabel, getDescription, value, onChange,
  placeholder = 'Search…', noun = 'results', disabled = false, pageSize = 25, ...rest
}: AsyncSearchSelectProps<T, F>) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const list = useServerList<T, F>({ fetchPage, filters, pageSize, enabled: open });

  useEffect(() => { setActive(0); }, [list.items]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const choose = (item: T | null) => {
    onChange(item);
    setOpen(false);
    list.setSearch('');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, list.items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const item = list.items[active]; if (item) choose(item); }
    else if (e.key === 'Escape') setOpen(false);
  };

  const shown = list.items.length;
  const more = list.total !== null ? list.total > shown : list.hasMore;

  return (
    <div ref={rootRef} className="relative min-w-0">
      {value && !open ? (
        <button type="button" disabled={disabled} onClick={() => setOpen(true)} aria-label={rest['aria-label']}
          className="w-full text-left border border-gray-300 rounded-md px-3 py-2 text-sm bg-white hover:border-gray-400 disabled:bg-gray-50">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-medium text-gray-900 truncate">{getLabel(value)}</div>
              {getDescription?.(value) && <div className="text-xs text-gray-500 truncate">{getDescription(value)}</div>}
            </div>
            <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
          </div>
        </button>
      ) : (
        <div className="relative">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            role="combobox"
            aria-expanded={open}
            aria-label={rest['aria-label']}
            autoFocus={open}
            disabled={disabled}
            value={list.search}
            onChange={e => { list.setSearch(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="w-full border border-gray-300 rounded-md pl-9 pr-8 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
          />
          {(list.search || value) && (
            <button type="button" onClick={() => choose(null)} aria-label="Clear selection"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {open && (
        <div role="listbox" className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
          {list.loading && shown === 0 && <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>}
          {list.error && <div className="px-3 py-2 text-sm text-red-600">{list.error}</div>}
          {!list.loading && !list.error && shown === 0 && (
            <div className="px-3 py-3 text-sm text-gray-500">{list.search ? `No ${noun} match “${list.search}”.` : `No ${noun} found.`}</div>
          )}
          {list.items.map((item, i) => (
            <button key={getKey(item)} type="button" role="option" aria-selected={value ? getKey(value) === getKey(item) : false}
              onMouseEnter={() => setActive(i)} onClick={() => choose(item)}
              className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 ${i === active ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
              <div className="text-sm font-medium text-gray-900 truncate">{getLabel(item)}</div>
              {getDescription?.(item) && <div className="text-xs text-gray-500 truncate">{getDescription(item)}</div>}
            </button>
          ))}
          {more && shown > 0 && (
            <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50">
              Showing {shown}{list.total !== null ? ` of ${list.total}` : ''} {noun}. Type to narrow the search.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
